import { useMemo } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import type { DateRange } from 'react-day-picker'

import { Card } from '@/components/ui/card.tsx'
import type { ExportProps } from '@/components/ui/export'
import { getFormattedDateRange } from '@/components/ui/export'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { TableCellEditor } from '@/modules/sales-editor/components/table-cell-editor'

import type { DetailedComparisonType } from '../../types'
import { detailedComparisonTableColumnTitles as titles } from './consts'
import { detailedComparisonColumns } from './use-columns'
import {
  detailedComparisonTableCellFormatter,
  getDetailedComparisonTableSheetData,
} from './utils'

interface Props {
  data: DetailedComparisonType[]
  isFetching: boolean
  date: DateRange
  showCents?: boolean
}

export const DetailedComparison = ({
  data,
  isFetching,
  showCents,
  date,
}: Props) => {
  const exportOptions = useMemo<ExportProps>(
    () => ({
      fileName: `Detailed Comparison, ${getFormattedDateRange(date)}`,
      getTableSheetData: () => getDetailedComparisonTableSheetData(data),
    }),
    [date, data],
  )

  const defaultColumn = useMemo<Partial<ColumnDef<DetailedComparisonType>>>(
    () => ({
      cell: (ctx) => (
        <TableCellEditor
          cellContext={ctx}
          format={detailedComparisonTableCellFormatter(ctx, showCents)}
        />
      ),
      header: (ctx) => titles[ctx.column.id as keyof typeof titles],
    }),
    [showCents],
  )

  if (isFetching) {
    return (
      <Card className="p-6">
        <Skeleton className="h-40 w-full" />
      </Card>
    )
  }

  return (
    <DataTable
      title="Detailed Comparison"
      data={data}
      columns={detailedComparisonColumns}
      enableSorting
      enableExport
      exportOptions={exportOptions}
      defaultColumn={defaultColumn}
      rowId={(row) => row.creatorId}
      testId="detailed-comparison"
    />
  )
}
